/**
 * Socket.io Adapter — Frontend RealtimeProvider
 * Sprint 5, Story 5.5
 *
 * Replaces Supabase Realtime with Socket.io client.
 * Swap this adapter in src/lib/realtime/index.ts to switch.
 */

import { io, Socket } from 'socket.io-client';
import type { RealtimeProvider, RealtimeChannel, PostgresChangesConfig, RealtimePayload } from './types';

const API_URL = import.meta.env?.VITE_API_URL || 'http://localhost:3001';

// Map namespace names to Socket.io connections
const NAMESPACE_MAP: Record<string, string> = {
  'production-core-sync': '/production',
  'commerce-logistics-sync': '/commerce',
  'config-sync': '/config',
  'chat-messages-realtime': '/chat',
  'user-role-sync': '/production', // user role changes go through production namespace
  'heartbeat-check': '/production', // heartbeat uses production namespace
};

class SocketIoRealtimeAdapter implements RealtimeProvider {
  channel(name: string): RealtimeChannel {
    const namespace = NAMESPACE_MAP[name] || '/production';
    return new SocketIoChannel(name, namespace);
  }

  removeChannel(channel: RealtimeChannel): void {
    (channel as SocketIoChannel).destroy();
  }
}

class SocketIoChannel implements RealtimeChannel {
  private socket: Socket | null = null;
  private listeners: Array<{
    config: PostgresChangesConfig;
    callback: (payload: RealtimePayload) => void;
  }> = [];
  private channelName: string;
  private namespace: string;

  constructor(name: string, namespace: string) {
    this.channelName = name;
    this.namespace = namespace;
  }

  on(
    _type: 'postgres_changes',
    config: PostgresChangesConfig,
    callback: (payload: RealtimePayload) => void
  ): RealtimeChannel {
    this.listeners.push({ config, callback });
    return this;
  }

  subscribe(callback?: (status: any) => void): RealtimeChannel {
    // Função: lê o token atual a cada (re)conexão, para o token novo do
    // refresh-context valer depois de uma queda.
    this.socket = io(`${API_URL}${this.namespace}`, {
      auth: (cb) => cb({ token: localStorage.getItem('auth_token') }),
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });

    this.socket.on('connect', () => {
      callback?.('SUBSCRIBED');
    });

    this.socket.on('connect_error', () => {
      callback?.('CHANNEL_ERROR');
    });

    this.socket.on('disconnect', () => {
      callback?.('CLOSED');
    });

    // Listen for postgres_changes events and route to matching listeners
    this.socket.on('postgres_changes', (payload: RealtimePayload) => {
      for (const listener of this.listeners) {
        const { config } = listener;

        // Match table
        if (config.table !== payload.table) continue;

        // Match event type
        if (config.event !== '*' && config.event !== payload.eventType) continue;

        // Match filter (simple eq filter support)
        if (config.filter) {
          const match = config.filter.match(/^(\w+)=eq\.(.+)$/);
          if (match) {
            const [, field, value] = match;
            if ((payload.new as any)?.[field] !== value) continue;
          }
        }

        listener.callback(payload);
      }
    });

    return this;
  }

  unsubscribe(): void {
    this.destroy();
  }

  destroy(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners = [];
  }
}

export const socketioRealtimeAdapter = new SocketIoRealtimeAdapter();
